import { yupResolver } from '@hookform/resolvers/yup';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Link from '@mui/icons-material/Link';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AuthMethod, ModelError_Item, ResourceType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { capitalize } from 'lodash-es';
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import VendorAccountDetails from '~components/Common/VendorAccountDetails';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import AutocompleteField from '~components/FormFields/v2/Autocomplete/AutocompleteField';
import Label from '~components/FormFields/v2/Label';
import PhoneCodeFlagInput from '~components/FormFields/v2/PhoneCodeFlagInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { applyInviteTypeErrors } from '~components/UserInvite/inviteTypeErrors';
import UserInviteTypeSelector, {
  InviteType,
} from '~components/UserInvite/UserInviteTypeSelector';
import { SystemRoles } from '~constants/enums';
import { FormStateChangeProps } from '~formsShared/types';
import { AdminUser } from '~hooks/admin/useUsersAdmin/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { Driver } from '~hooks/useDrivers/models';
import { BasicEquipment, EquipmentTypeahead } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { BasicLabel } from '~hooks/useLabels/models';
import useLabels from '~hooks/useLabels/useLabels';
import { ResourceScope } from '~hooks/useResourceScopes/models';
import { useResourceScopes } from '~hooks/useResourceScopes/useResourceScopes';
import { Site } from '~hooks/useSites/models';
import { useSites } from '~hooks/useSites/useSites';
import { RoleItem, User } from '~hooks/useUsers/models';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import { getEquipmentLabel } from '~pages/Settings/UserManagement/EquipmentSelector';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { allowedTimezones } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  driverFormValidationSchema,
  userEmailSchema,
  userPhoneSchema,
  userWithEmailAndOmitedPhoneValidationSchema,
  userWithEmailOrPhoneValidationSchema,
  userWithRequiredEmailAndOmitedPhoneValidationSchema,
  userWithRequiredEmailValidationSchema,
} from '../userFormValidationSchema';

// users having at least one of these roles can be shared across companies
export const SHAREABLE_ROLES = [SystemRoles.DRIVER, SystemRoles.FOREMAN].map((role) => {
  return role.toLowerCase();
});

export interface UserFormHandler {
  submit: (callback: (data: UserValidationDTOWithSitesDiff) => void) => void;
  resetForm: (callback?: () => void) => void;
}

type UserValidationSchema =
  | typeof userWithRequiredEmailValidationSchema
  | typeof userWithRequiredEmailAndOmitedPhoneValidationSchema
  | typeof userWithEmailOrPhoneValidationSchema
  | typeof userWithEmailAndOmitedPhoneValidationSchema;

type UserValidationDTO =
  | yup.InferType<typeof userWithRequiredEmailValidationSchema>
  | yup.InferType<typeof userWithRequiredEmailAndOmitedPhoneValidationSchema>
  | yup.InferType<typeof userWithEmailOrPhoneValidationSchema>
  | yup.InferType<typeof userWithEmailAndOmitedPhoneValidationSchema>;

type SitesDiff = { resourceIdsToDelete: string[]; siteIdsToAdd: string[] };

export type UserValidationDTOWithSitesDiff = UserValidationDTO & {
  sitesDiff: SitesDiff;
  accountName?: string;
  accountExternalId?: string;
  defaultDriverEquipment?: BasicEquipment | null;
  additionalEquipment?: BasicEquipment | null;
  defaultDriverAdditionalEquipment?: BasicEquipment | null;
  // User-shaped rate-visibility flag, only emitted when the viewer holds
  // edit_user_hide_rate — absent means "no change" (see the submit handler)
  hideRates?: boolean;
  intrastateHoursOfService?: boolean;
};

export interface UserFormStateChangeProps extends FormStateChangeProps {
  hasValidEmail: boolean;
  hasValidPhone: boolean;
  hasDriverRole: boolean;
}

// Where this form is being rendered from
enum UserFormSource {
  ADMIN_USERS = 'admin_users',
}

interface UserProfileFormProviderProps {
  availableAuthMethods: AuthMethod[];
  defaultUser: Nullable<User | AdminUser>;
  defaultDriver?: Driver;
  editable: boolean;
  disableUserRolesField?: boolean;
  userRolesOptions?: RoleItem[];
  onFormStateChange: ({ hasValidEmail, hasValidPhone }: UserFormStateChangeProps) => void;
  allowSharing?: boolean;
  companyShares?: Array<CompanyShare>;
  source?: `${UserFormSource}`;
  errors?: ModelError_Item[];
  isManagedUser?: boolean;
  scopedCompanyId?: string;
  managedCompanyId?: string;
  showInviteTypeSelector?: boolean;
  inviteTypes?: InviteType[];
  inviteTypeError?: string;
  onInviteTypesChange?: (value: InviteType[]) => void;
}

interface UserProfileFormProps extends Omit<
  UserProfileFormProviderProps,
  'availableAuthMethods'
> {
  validationSchema: UserValidationSchema;
  sites: ResourceScope[];
}

// Once entered, phone number cannot be edited, but we have registered users with invalid numbers.
// This is blocking admins from editing user's fields different from phone number, so for those
// Cases we skip the validation.
const shouldSkipPhoneNumberValidation = (phoneNumber: Nullable<string>) => {
  return Boolean(phoneNumber);
};

const validateEmail = (email: Nullable<string>) => {
  let isValid = true;

  try {
    const input = email || '';

    if (input.length) {
      userEmailSchema.validateSync(input);
    } else {
      isValid = false;
    }
  } catch {
    isValid = false;
  }

  return isValid;
};

const validatePhone = (phone: Nullable<string>) => {
  let isValid = true;

  try {
    const input = phone || '';

    if (input.length) {
      userPhoneSchema.validateSync(input);
    } else {
      isValid = false;
    }
  } catch {
    isValid = false;
  }

  return isValid;
};

type ResourceScopesState = {
  hasFailed: boolean;
  isLoading: boolean;
  sites: ResourceScope[];
};

const UserFormProvider = forwardRef(function UserFormProvider(
  {
    availableAuthMethods,
    defaultUser,
    editable,
    source,
    scopedCompanyId,
    managedCompanyId,
    ...rest
  }: UserProfileFormProviderProps,
  ref: Ref<UserFormHandler>,
) {
  const isEditing = Boolean(defaultUser?.id);

  const { getAllResourceScopes } = useResourceScopes();
  const [resourceScopes, setResourceScopes] = useState<ResourceScopesState>({
    hasFailed: false,
    isLoading: isEditing,
    sites: [],
  });

  useEffect(() => {
    if (isEditing) {
      setResourceScopes((state) => ({ ...state, isLoading: true }));

      const loadResourceScopes = async () => {
        try {
          const response = await getAllResourceScopes({
            userId: String(defaultUser?.id),
            resourceType: ResourceType.SITE,
            companyId: managedCompanyId ? scopedCompanyId : defaultUser?.company?.id,
            managedCompanyId,
          });

          setResourceScopes({
            hasFailed: false,
            isLoading: false,
            sites: response.reduce((acc, resourceScope) => {
              if (resourceScope.resource.type === ResourceType.SITE) {
                acc.push(resourceScope);
              }

              return acc;
            }, [] as ResourceScope[]),
          });
        } catch {
          setResourceScopes({ hasFailed: true, isLoading: false, sites: [] });
        }
      };

      void loadResourceScopes();
    }
  }, [isEditing]);

  const validationSchema = useMemo(() => {
    const skipPhoneNumberValidation = shouldSkipPhoneNumberValidation(defaultUser?.phone);

    if (availableAuthMethods.includes(AuthMethod.OTP_SMS)) {
      return skipPhoneNumberValidation
        ? userWithEmailAndOmitedPhoneValidationSchema
        : userWithEmailOrPhoneValidationSchema;
    }

    return skipPhoneNumberValidation
      ? userWithRequiredEmailAndOmitedPhoneValidationSchema
      : userWithRequiredEmailValidationSchema;
  }, [availableAuthMethods, defaultUser?.phone]);

  if (resourceScopes.isLoading) {
    return <LoadingSpinner isVisible />;
  }

  // An empty site list reads as "all sites visible", so a failed fetch must not render the form.
  if (resourceScopes.hasFailed) {
    return null;
  }

  return (
    <UserForm
      ref={ref}
      editable={editable}
      validationSchema={validationSchema}
      defaultUser={defaultUser}
      source={source}
      sites={resourceScopes.sites}
      scopedCompanyId={scopedCompanyId}
      managedCompanyId={managedCompanyId}
      {...rest}
    />
  );
});

const UserForm = forwardRef(function UserForm(
  {
    allowSharing = false,
    companyShares,
    defaultDriver,
    defaultUser,
    disableUserRolesField,
    userRolesOptions: userRolesOptionsProp,
    editable,
    errors: errorsProp,
    onFormStateChange,
    sites,
    source,
    validationSchema,
    isManagedUser = false,
    scopedCompanyId,
    managedCompanyId,
    showInviteTypeSelector = false,
    inviteTypes,
    inviteTypeError,
    onInviteTypesChange,
  }: UserProfileFormProps,
  ref: Ref<UserFormHandler>,
) {
  const { userStore } = useStores();
  const companyId = scopedCompanyId || getEffectiveUserCompanyId(userStore);
  const { getAllConnectedCompaniesByCompanyIdTypeahead } = useCompany();
  const { getCompanySitesTypeahead } = useSites();
  const { getEquipmentByCompanyIdTypeahead } = useEquipment();
  const getEquipmentByCompanyIdTypeaheadSafe = useCallback(
    async (options: Parameters<typeof getEquipmentByCompanyIdTypeahead>[0]) => {
      try {
        return await getEquipmentByCompanyIdTypeahead(options);
      } catch {
        // Error is already handled in the hook
        return { data: [], pagination: undefined };
      }
    },
    [getEquipmentByCompanyIdTypeahead],
  );
  const { fetchLabelsTypeahead } = useLabels();
  const showDriverTrailerSettingEnabled =
    userStore.companySettings.isEnabled('show_driver_trailer');
  const theme = useTheme();

  const isSharedDriver = Boolean(defaultDriver?.companyShare?.id);
  const userRolesOptions = (userRolesOptionsProp ?? userStore.userRoles).map((role) => ({
    label: role.name,
    value: role.id,
    _entity: role,
  }));
  const timezoneOptions = allowedTimezones.map((tz) => ({
    label: tz,
    value: tz,
    _entity: tz,
  }));

  const isManagedDriver = defaultDriver?.managed;
  const defaultSites = sites.map((site) => ({
    id: site.resource.id,
    name: site.resource.name,
    resourceId: String(site.id),
  }));

  const defaultTimezone = defaultUser?.timeZone || userStore.userCompany?.timeZone || '';
  const canEditUserHideRate = userStore.getPermissions().canEditUserHideRate;
  const defaultUserValues = {
    firstName: defaultUser?.firstName || '',
    lastName: defaultUser?.lastName || '',
    userRoles: (defaultUser?.userRoles || []).map((role) => ({
      label: role.name,
      value: role.id,
      _entity: role,
    })),
    phone: defaultUser?.phone,
    email: defaultUser?.email,
    company: defaultUser?.company || userStore.currentCompanies[0] || null,
    timeZone: {
      label: defaultTimezone,
      value: defaultTimezone,
      _entity: defaultTimezone,
    },
    textToAcceptFieldEnabled: source === UserFormSource.ADMIN_USERS,
    textToAcceptFieldValue: defaultUser?.textToAcceptEnabled,
    hideRatesFieldEnabled: canEditUserHideRate,
    hideRatesFieldValue: defaultUser?.hideRates,
    externalId: defaultUser?.externalId,
    accountingId: defaultUser?.accountingId,
    equipment: defaultUser?.equipment,
    labels: defaultUser?.labels ?? [],
  };

  const defaultDriverValues = {
    firstName: defaultDriver?.firstName || '',
    lastName: defaultDriver?.lastName || '',
    phone: defaultDriver?.phone,
    email: defaultDriver?.email,
    company: defaultDriver?.company || null,
    accountName: defaultDriver?.companyShare?.receiverAccount?.name || '',
    accountExternalId: defaultDriver?.companyShare?.receiverAccount?.externalId || '',
    equipment: defaultDriver?.equipment,
    defaultDriverEquipment: defaultDriver?.companyShare?.defaultEquipment || null,
    additionalEquipment: defaultDriver?.additionalEquipment?.[0] || null,
    defaultDriverAdditionalEquipment:
      defaultDriver?.companyShare?.defaultAdditionalEquipment?.[0] || null,
    labels: defaultDriver?.labels ?? [],
    // Same eligibility as the rest of the driver's own details (canEditDriverDetails in
    // EditDriverDrawer): editable for an internal or managed driver, not a vendor's shared driver
    hideRatesFieldEnabled: canEditUserHideRate && (!isSharedDriver || isManagedDriver),
    hideRatesFieldValue: defaultDriver?.hideRates,
    intrastateHoursOfService: defaultDriver?.intrastateHoursOfService,
  };

  const defaultValues = defaultDriver ? defaultDriverValues : defaultUserValues;
  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(defaultDriver ? driverFormValidationSchema : validationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      ...defaultValues,
      companyShares: companyShares?.length
        ? companyShares.map((companyShare) => ({
            id: companyShare.id,
            receiverCompany: companyShare.receiverCompany,
            rateOverride: companyShare.rateOverride ?? false,
          }))
        : [],
      sites: defaultSites,
    },
  });

  const watchedCompany = watch('company');
  const watchedUserRoles = watch('userRoles');
  const watchedEmail = watch('email');
  const watchedPhone = watch('phone');
  const watchedSites = watch('sites') as unknown as Site[] | null;

  const {
    fields: companySharesFields,
    append: appendCompanyShares,
    remove: removeCompanyShares,
  } = useFieldArray({
    control,
    name: 'companyShares',
  });

  const sitesDiff: SitesDiff = useMemo(() => {
    const resourceIdsToDelete = defaultSites.reduce((acc, defaultSite) => {
      const site = watchedSites?.find((site) => site.id === defaultSite.id);

      if (!site) {
        acc.push(defaultSite.resourceId);
      }

      return acc;
    }, [] as string[]);

    const siteIdsToAdd = (watchedSites ?? []).reduce((acc, watchedSite) => {
      const site = defaultSites.find((site) => site.id === watchedSite.id);

      if (!site) {
        acc.push(watchedSite.id);
      }

      return acc;
    }, [] as string[]);

    return { resourceIdsToDelete, siteIdsToAdd };
  }, [defaultSites, watchedSites]);

  // Update default values once async companyShares are loaded
  useEffect(() => {
    if (companyShares?.length) {
      const updatedDefaultValues = {
        ...defaultValues,
        companyShares: companyShares.map((companyShare) => ({
          id: companyShare.id,
          receiverCompany: companyShare.receiverCompany,
          rateOverride: companyShare.rateOverride ?? false,
        })),
      };
      reset(updatedDefaultValues);
    }
  }, [companyShares, reset, JSON.stringify(defaultValues)]);

  const hasValidEmail = useMemo(() => {
    return validateEmail(watchedEmail);
  }, [watchedEmail]);

  const hasValidPhone = useMemo(() => {
    return validatePhone(watchedPhone);
  }, [watchedPhone]);

  const hasShareableRole = useMemo(() => {
    return (
      watchedUserRoles &&
      watchedUserRoles.some((role) => SHAREABLE_ROLES.includes(role.label.toLowerCase()))
    );
  }, [watchedUserRoles]);

  const hasDriverRole = useMemo(() => {
    return (
      watchedUserRoles &&
      watchedUserRoles.some(
        (role) => role.label.toLowerCase() === SystemRoles.DRIVER.toLowerCase(),
      )
    );
  }, [watchedUserRoles]);

  const hasViewerRole = useMemo(() => {
    return (
      watchedUserRoles &&
      watchedUserRoles.some((role) => role.label.toLowerCase() === 'viewer')
    );
  }, [watchedUserRoles]);

  const { isEmailFieldOptional, isPhoneFieldOptional } = useMemo(() => {
    const emailDesc = validationSchema.describe().fields.email as yup.SchemaDescription;
    const phoneDesc = validationSchema.describe().fields.phone as yup.SchemaDescription;

    return {
      isEmailFieldOptional: emailDesc ? emailDesc.nullable || emailDesc.optional : true,
      isPhoneFieldOptional: phoneDesc ? phoneDesc.nullable || phoneDesc.optional : true,
    };
  }, [validationSchema]);

  useEffect(() => {
    if (!hasViewerRole) {
      setValue('sites', []);
    }
  }, [hasViewerRole]);

  const routeInviteTypeError = () =>
    applyInviteTypeErrors({
      clearEmailError: () => clearErrors('email'),
      email: watchedEmail,
      hasValidEmail,
      hasValidPhone,
      inviteTypes: inviteTypes ?? [],
      setEmailError: (message) => setError('email', { type: 'manual', message }),
      setPhoneError: (message) => setError('phone', { type: 'manual', message }),
    });

  useImperativeHandle(ref, () => ({
    submit(callback) {
      handleSubmit((data) => {
        if (routeInviteTypeError()) return;
        callback({
          ...data,
          sitesDiff,
          ...('userRoles' in data
            ? { userRoles: data.userRoles.map((role) => role._entity) }
            : {}),
          ...('timeZone' in data ? { timeZone: data.timeZone.value } : {}),
          // hideRatesFieldEnabled is only true when the viewer holds edit_user_hide_rate,
          // so hideRates is omitted (not false) when the viewer can't change it
          ...('hideRatesFieldEnabled' in data && data.hideRatesFieldEnabled
            ? { hideRates: Boolean(data.hideRatesFieldValue) }
            : {}),
        } as UserValidationDTOWithSitesDiff);
      }, routeInviteTypeError)();
    },
    resetForm(callback) {
      reset();
      callback?.();
    },
  }));

  useEffect(() => {
    onFormStateChange({
      isDirty: true,
      hasValidEmail,
      hasValidPhone,
      hasDriverRole: !!hasDriverRole,
    });
  }, [hasValidEmail, hasValidPhone, hasDriverRole]);

  useEffect(() => {
    if (errorsProp) {
      for (const error of errorsProp) {
        const newErrorField = getErrorMapping(error.field, error.message);
        if (newErrorField.field && newErrorField.message) {
          setError(newErrorField.field as keyof ReturnType<typeof setError>, {
            type: 'manual',
            message: capitalize(newErrorField.message as string),
          });
        }
      }
    }
  }, [errorsProp]);

  return (
    <>
      <Grid container spacing={2}>
        {!isManagedUser && (
          <Grid size={{ xs: 12 }}>
            <CompanySelectorFormField
              sx={{ width: '100%', minWidth: '100%' }}
              name={'company'}
              isRequired={true}
              disabled={!!defaultUser?.company?.id || !!defaultDriver?.company?.id}
              errors={errors}
              control={control}
            />
          </Grid>
        )}

        {defaultDriver && !!defaultDriver.companyShare && (
          <Grid size={{ xs: 12 }}>
            <VendorAccountDetails control={control} errors={errors} isEditing={true} />
          </Grid>
        )}

        <Grid size={{ xs: 6 }}>
          <TextInput
            control={control}
            isDisabled={!editable || (isSharedDriver && !isManagedDriver)}
            isRequired
            label={t('form_fields.first_name')}
            name="firstName"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextInput
            control={control}
            isDisabled={!editable || (isSharedDriver && !isManagedDriver)}
            isRequired
            label={t('form_fields.last_name')}
            name="lastName"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextInput
            control={control}
            isDisabled={!editable || (isSharedDriver && !isManagedDriver)}
            isRequired={!isEmailFieldOptional || !!inviteTypes?.includes('email')}
            label={t('form_fields.email')}
            name="email"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <PhoneCodeFlagInput
            control={control}
            isDisabled={!editable || (isSharedDriver && !isManagedDriver)}
            isRequired={!isPhoneFieldOptional || !!inviteTypes?.includes('sms')}
            label={t('form_fields.phone')}
            name="phone"
          />
        </Grid>
        {showInviteTypeSelector && !defaultDriver && onInviteTypesChange && (
          <Grid size={{ xs: 12 }}>
            <UserInviteTypeSelector
              email={watchedEmail}
              error={inviteTypeError}
              isDisabled={!editable}
              isEmailEnabled={hasValidEmail}
              isPhoneEnabled={hasValidPhone}
              onChange={onInviteTypesChange}
              phone={watchedPhone}
              value={inviteTypes ?? []}
            />
          </Grid>
        )}

        {defaultDriver && (
          <Grid size={{ xs: 6 }}>
            <AutocompleteAsyncFormField
              control={control}
              errors={errors}
              name={defaultDriver.companyShare ? 'defaultDriverEquipment' : 'equipment'}
              getValue={(item) => item?.id || ''}
              getLabel={(item: EquipmentTypeahead) =>
                item ? getEquipmentLabel(item) : ''
              }
              asyncCallback={getEquipmentByCompanyIdTypeaheadSafe}
              extraRequestOptions={{
                companyId: companyId,
                managedCompanyId,
                dispatchable: true,
              }}
              filterSelectedOptions={false}
              clearable
              label={t('form_fields.available_equipment')}
              disablePortal={false}
              inputProps={{
                sx: {
                  fontSize: '14px',
                },
              }}
              disabled={!editable}
              placeholder={`${t('form_fields.select_truck')}`}
              defaultValue={
                defaultDriver.companyShare
                  ? defaultDriverValues.defaultDriverEquipment
                  : defaultDriverValues.equipment
              }
            />
          </Grid>
        )}
        {defaultDriver && showDriverTrailerSettingEnabled && (
          <Grid size={{ xs: 6 }}>
            <AutocompleteAsyncFormField
              control={control}
              errors={errors}
              name={
                defaultDriver.companyShare
                  ? 'defaultDriverAdditionalEquipment'
                  : 'additionalEquipment'
              }
              getValue={(item) => item?.id || ''}
              getLabel={(item: EquipmentTypeahead) =>
                item ? getEquipmentLabel(item) : ''
              }
              asyncCallback={getEquipmentByCompanyIdTypeaheadSafe}
              extraRequestOptions={{ companyId, managedCompanyId, dispatchable: true }}
              filterSelectedOptions={false}
              clearable
              label={t('form_fields.trailer')}
              disablePortal={false}
              inputProps={{ sx: { fontSize: '14px' } }}
              disabled={!editable}
              placeholder={`${t('form_fields.select_trailer')}`}
              defaultValue={
                defaultDriver.companyShare
                  ? defaultDriverValues.defaultDriverAdditionalEquipment
                  : defaultDriverValues.additionalEquipment
              }
            />
          </Grid>
        )}
        {defaultDriver && (
          <Grid size={{ xs: 12 }}>
            <TextInput
              control={control}
              isDisabled={!editable || isSharedDriver}
              label={t('form_fields.external_id')}
              name="externalId"
            />
          </Grid>
        )}
        {defaultDriver && (
          <Grid size={{ xs: 12 }}>
            <AutocompleteAsyncFormField
              asyncCallback={fetchLabelsTypeahead}
              control={control}
              errors={errors}
              extraRequestOptions={{ companyId }}
              getLabel={(label: BasicLabel) => label.name}
              getValue={(label: BasicLabel) => label.id}
              label={t('form_fields.labels')}
              multiple
              limitTags={3}
              name="labels"
            />
          </Grid>
        )}
        {defaultDriver && (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
              <Label>{t('form_fields.hours_of_service')}</Label>
              <Tooltip
                arrow
                describeChild
                placement="top"
                title={t('form_fields.hours_of_service_tooltip')}
              >
                <InfoOutlined
                  sx={{ color: 'text.secondary', cursor: 'help', fontSize: 14 }}
                />
              </Tooltip>
            </Box>
            <Controller
              control={control}
              name="intrastateHoursOfService"
              render={({ field }) => (
                <RadioGroup
                  aria-label={t('form_fields.hours_of_service')}
                  onChange={(event) => field.onChange(event.target.value === 'true')}
                  row
                  value={String(Boolean(field.value))}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        disabled={!editable || (isSharedDriver && !isManagedDriver)}
                      />
                    }
                    label={t('form_fields.interstate')}
                    value="false"
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        disabled={!editable || (isSharedDriver && !isManagedDriver)}
                      />
                    }
                    label={t('form_fields.intrastate')}
                    value="true"
                  />
                </RadioGroup>
              )}
            />
          </Grid>
        )}

        {!defaultDriver && (
          <>
            <Grid size={{ xs: 6 }}>
              <AutocompleteField
                control={control}
                isDisabled={disableUserRolesField || !editable || isSharedDriver}
                isMultiple
                isRequired
                label={t('form_fields.role')}
                limitTags={2}
                name="userRoles"
                options={userRolesOptions}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <AutocompleteField
                control={control}
                disableClearable
                isDisabled={!editable || isSharedDriver}
                isRequired
                label={t('form_fields.time_zone')}
                name="timeZone"
                options={timezoneOptions}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextInput
                control={control}
                isDisabled={!editable || isSharedDriver}
                label={t('form_fields.external_id')}
                name="externalId"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextInput
                control={control}
                isDisabled={!editable || isSharedDriver}
                label={t('form_fields.accounting_id')}
                name="accountingId"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <AutocompleteAsyncFormField
                asyncCallback={fetchLabelsTypeahead}
                control={control}
                errors={errors}
                extraRequestOptions={{ companyId }}
                getLabel={(label: BasicLabel) => label.name}
                getValue={(label: BasicLabel) => label.id}
                label={t('form_fields.labels')}
                multiple
                limitTags={3}
                name="labels"
              />
            </Grid>

            {getValues('textToAcceptFieldEnabled') && (
              <Grid size={{ xs: 12 }}>
                <CheckBoxFormField
                  control={control}
                  disabled={!editable || isSharedDriver}
                  label={`${t('form_fields.text_to_accept_enabled')}`}
                  name="textToAcceptFieldValue"
                  value={Boolean(getValues('textToAcceptFieldValue'))}
                />
              </Grid>
            )}
          </>
        )}

        {getValues('hideRatesFieldEnabled') && (
          <Grid size={{ xs: 12 }}>
            <CheckBoxFormField
              control={control}
              disabled={!editable}
              errors={errors}
              label={`${t('form_fields.hide_rates')}`}
              name="hideRatesFieldValue"
              value={Boolean(getValues('hideRatesFieldValue'))}
            />
          </Grid>
        )}
      </Grid>
      {/* Viewer site selection */}
      {hasViewerRole && (
        <Grid container sx={{ mt: 2 }}>
          <Typography variant="form-section-title" sx={{ mb: 1, fontSize: '14px' }}>
            {t('user.form.site_permissions')}
          </Typography>

          <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 6 }}>
              <AutocompleteAsyncFormField
                asyncCallback={getCompanySitesTypeahead}
                control={control}
                disabled={!editable || isSharedDriver}
                errors={errors}
                extraRequestOptions={{
                  companyId: watchedCompany?.id || '',
                  onlyActiveSites: true,
                }}
                filterSelectedOptions={false}
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                hideChipOverflow
                hideLabel
                limitTags={1}
                multiple
                name="sites"
                renderOption={(props, option, state) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Checkbox checked={state.selected} sx={{ mr: 1, padding: 0 }} />
                      {option.companyShare && (
                        <Link
                          sx={{
                            color: theme.brandV2.colors.treadGreen,
                            fontSize: '20px',
                            mr: 1,
                          }}
                        />
                      )}
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }} sx={{ mt: -1 }}>
              <Typography
                variant={'caption'}
                color={'secondary'}
                sx={{
                  fontWeight: 700,
                }}
              >
                {t('user.form.site_access_list')}
              </Typography>
              <Typography variant={'body2'}>
                {watchedSites && watchedSites.length > 0
                  ? watchedSites.map((site) => site.name).join(', ')
                  : t('user.form.all_sites_visible')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
      {/* Shared user configuration */}
      {(hasShareableRole || !!defaultDriver) && allowSharing && (
        <Grid container sx={{ mt: 2 }}>
          <Typography variant="form-section-title" sx={{ mb: 1 }}>
            {t('user.form.shared_driver_configuration')}
          </Typography>
          <Typography variant={'body2'} sx={{ mb: 2 }}>
            {t('user.form.shared_driver_configuration_hint')}
          </Typography>

          {isSharedDriver && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mb: 2,
                width: '100%',
                border: `1px solid ${theme.palette.divider}`,
                padding: '5px',
              }}
            >
              <Typography variant={'body2'}>
                {t('user.form.shared_from_company')}{' '}
                {defaultDriver?.companyShare?.senderCompany?.legalName}
              </Typography>
            </Box>
          )}

          {/* Company Shares */}
          {companySharesFields.map((item, index) => {
            return (
              <Grid container size={{ xs: 12 }} spacing={2} key={item.id}>
                <Grid size={{ xs: 6 }}>
                  <AutocompleteAsyncFormField
                    control={control}
                    errors={errors}
                    name={`companyShares[${index}].receiverCompany`}
                    getValue={(item) => item.id}
                    getLabel={(item) => item.legalName}
                    asyncCallback={getAllConnectedCompaniesByCompanyIdTypeahead}
                    extraRequestOptions={{
                      companyId: managedCompanyId
                        ? companyId
                        : isSharedDriver
                          ? userStore.currentCompanies[0].id
                          : watchedCompany?.id || '',
                      managedCompanyId,
                    }}
                    label={`${t('user.form.select_share_to_companies')}`}
                  />
                </Grid>
                <Grid size={{ xs: 5 }}>
                  <CheckBoxFormField
                    control={control}
                    errors={errors}
                    name={`companyShares[${index}].rateOverride`}
                    label={`${t('form_fields.share_pay_rate')}`}
                    value={false}
                    sx={{ mt: 2 }}
                  />
                </Grid>

                <Grid size={{ xs: 1 }}>
                  <RemoveItemButton
                    action={() => {
                      removeCompanyShares(index);
                    }}
                    sx={{ ml: 1, mt: 3, cursor: 'pointer' }}
                  />
                </Grid>
              </Grid>
            );
          })}

          <PseudoLink
            title={`+ ${t('administration.equipment.add_shared_company')}`}
            sx={{ mt: 2, display: 'inline-flex' }}
            action={() => {
              appendCompanyShares({
                id: '',
                receiverCompany: { id: '', legalName: '' } as CompanyBasic,
                rateOverride: false,
              });
            }}
          />
        </Grid>
      )}
    </>
  );
});

const getErrorMapping = (fieldName: string, defaultMessage: string) => {
  switch (fieldName) {
    case 'base':
      return {
        field: 'firstName',
        message: defaultMessage,
      };
    case 'first_name':
      return {
        field: 'firstName',
        message: defaultMessage,
      };
    case 'last_name':
      return {
        field: 'lastName',
        message: defaultMessage,
      };
    case 'hide_rates':
      return {
        field: 'hideRatesFieldValue',
        message: defaultMessage,
      };
    default:
      return {
        field: fieldName,
        message: defaultMessage,
      };
  }
};

export { UserFormProvider as UserForm };
